import { forwardRef } from "react";
import type { LucideProps } from "lucide-react";
import {
  BfAdminPixelIcon,
  type BfAdminIconName,
} from "@bitsandflames/web-components/layouts";

// Match the BF ecosystem's 16 × 16 integer grid. Labels stay on the controls;
// these silhouettes are decorative and share their existing icon prop contract.
function icon(name: string, paths: string[], admin?: BfAdminIconName) {
  const Icon = forwardRef<SVGSVGElement, LucideProps>(function PixelIcon(
    {
      size = 24,
      absoluteStrokeWidth: _absolute,
      strokeWidth: _stroke,
      ...props
    },
    ref,
  ) {
    return (
      <svg
        {...props}
        ref={ref}
        width={props.width ?? size}
        height={props.height ?? size}
        viewBox="0 0 16 16"
        fill="currentColor"
        stroke="none"
        shapeRendering="crispEdges"
        aria-hidden={
          props["aria-label"] || props["aria-labelledby"] ? undefined : true
        }
        focusable="false"
      >
        {admin ? (
          <BfAdminPixelIcon name={admin} size={16} />
        ) : (
          paths.map((d, i) => <path key={i} d={d} fillRule="evenodd" />)
        )}
      </svg>
    );
  });
  Icon.displayName = `Pixel${name}`;
  return Icon;
}
const ring =
  "M4 1h8v1h2v2h1v8h-1v2h-2v1H4v-1H2v-2H1V4h1V2h2zM4 3v1H3v8h1v1h8v-1h1V4h-1V3z";
const check = "M1 7h2v2h2v2h2V9h2V7h2V5h2V3h2v4h-2v2h-2v2H9v2H7v2H5v-2H3v-2H1z";
const smallCheck = "M4 7h2v2h2V7h2V5h2v4h-2v2H8v2H6v-2H4z";
const file = "M3 1h7v1h2v2h1v11H3zM5 3v10h6V5H9V3z";
const arrowRight = "M8 1h2v2h2v2h2v2h2v2h-2v2h-2v2h-2v2H8v-4h2V9H0V7h10V5H8z";
const arrowLeft = "M6 1h2v4H6v2h10v2H6v2h2v4H6v-2H4v-2H2V9H0V7h2V5h2V3h2z";
const northeast = "M6 1h9v9h-2V5h-2v2H9v2H7v2H5v2H3v2H1v-4h2V9h2V7h2V5h2V3H6z";
const upload =
  "M7 1h2v2h2v2h2v2h-3V5H9v6H7V5H6v2H3V5h2V3h2zM1 10h2v3h10v-3h2v5H1z";
const download =
  "M7 1h2v6h1V5h3v2h-2v2H9v2H7V9H5V7H3V5h3v2h1zM1 10h2v3h10v-3h2v5H1z";
const lock = "M5 1h6v1h1v5h2v8H2V7h2V2h1zM6 3v4h4V3zM7 10v3h2v-3z";
export const Plus = icon("Plus", ["M7 1h2v6h6v2H9v6H7V9H1V7h6z"]);
export const Minus = icon("Minus", ["M1 7h14v2H1z"]);
export const X = icon("X", [
  "M2 1h2v2h2v2h4V3h2V1h2v4h-2v2h-2v2h2v2h2v4h-2v-2h-2v-2H6v2H4v2H2v-4h2V9h2V7H4V5H2z",
]);
export const Check = icon("Check", [check]);
export const CheckCircle2 = icon("CheckCircle2", [ring, smallCheck]);
export const CircleCheck = CheckCircle2;
export const XCircle = icon("XCircle", [
  ring,
  "M5 5h2v2h2V5h2v2H9v2h2v2H9V9H7v2H5V9h2V7H5z",
]);
export const ChevronRight = icon("ChevronRight", [
  "M5 1h2v2h2v2h2v2h2v2h-2v2H9v2H7v2H5v-4h2V9h2V7H7V5H5z",
]);
export const ChevronDown = icon("ChevronDown", [
  "M1 5h4v2h2v2h2V7h2V5h4v2h-2v2h-2v2H9v2H7v-2H5V9H3V7H1z",
]);
export const ChevronUp = icon("ChevronUp", [
  "M7 3h2v2h2v2h2v2h2v2h-4V9H9V7H7v2H5v2H1V9h2V7h2V5h2z",
]);
export const ArrowRight = icon("ArrowRight", [arrowRight]);
export const ArrowLeft = icon("ArrowLeft", [arrowLeft]);
export const ArrowUpRight = icon("ArrowUpRight", [northeast]);
export const ExternalLink = icon("ExternalLink", [
  "M1 4h5v2H3v7h7v-3h2v5H1z",
  "M8 1h7v7h-2V5h-2v2H9v2H7V7h2V5h2V3H8z",
]);
export const Upload = icon("Upload", [upload]);
export const Download = icon("Download", [download]);
export const Send = icon("Send", [
  "M1 2h14v12H1zM3 4v8h10V4z",
  "M3 4h2v2h2v2h2V6h2V4h2v2h-2v2H9v2H7V8H5V6H3z",
]);
export const Save = icon("Save", ["M1 1h12v1h2v13H1zM4 3v3h6V3zM4 9v4h8V9z"]);
export const Copy = icon("Copy", ["M1 1h10v2H3v8H1zM5 5h10v10H5zM7 7v6h6V7z"]);
export const Trash2 = icon("Trash2", [
  "M5 1h6v2h4v2H1V3h4zM3 6h10v9H3zM5 8v5h2V8zM9 8v5h2V8z",
]);
export const Archive = icon("Archive", [
  "M1 1h14v5H1zM3 3v1h10V3zM2 7h12v8H2zM4 9v4h8V9zM6 10h4v2H6z",
]);
export const ClipboardCheck = icon("ClipboardCheck", [
  "M5 0h6v2h3v14H2V2h3zM6 2v2h4V2zM4 4v10h8V4h-1v2H5V4z",
  "M5 8h2v2h2V8h2v4H7v-1H5z",
]);
export const Type = icon("Type", ["M1 2h14v4h-2V4H9v9h3v2H4v-2h3V4H3v2H1z"]);
export const AlignLeft = icon("AlignLeft", [
  "M1 2h14v2H1zM1 6h9v2H1zM1 10h14v2H1zM1 14h9v2H1z",
]);
export const Hash = icon("Hash", [
  "M4 1h2v3h4V1h2v3h3v2h-3v4h3v2h-3v3h-2v-3H6v3H4v-3H1v-2h3V6H1V4h3zM6 6v4h4V6z",
]);
export const CalendarDays = icon("CalendarDays", [
  "M4 0h2v2h4V0h2v2h3v14H1V2h3zM3 4v2h10V4zM3 8v6h10V8z",
  "M5 9h2v2H5zM9 9h2v2H9zM5 12h2v1H5zM9 12h2v1H9z",
]);
export const Clock3 = icon("Clock3", [ring, "M7 4h2v4h3v2H7z"]);
export const History = icon("History", [], "audit");
export const Info = icon("Info", [ring, "M7 4h2v2H7zM7 7h2v5H7z"]);
export const AlertCircle = icon("AlertCircle", [
  ring,
  "M7 4h2v5H7zM7 11h2v2H7z",
]);
export const AlertTriangle = icon("AlertTriangle", [
  "M7 0h2v2h2v3h2v3h2v3h1v5H0v-5h1V8h2V5h2V2h2zM7 4v3H5v3H3v4h10v-4h-2V7H9V4z",
  "M7 7h2v3H7zM7 12h2v1H7z",
]);
export const Layers3 = icon("Layers3", [
  "M5 0h6v2h4v2h1v2h-4v2H4V6H0V4h1V2h4zM5 2v2H3v1h2v1h6V5h2V4h-2V2zM0 8h4v2h8V8h4v2h-4v2H4v-2H0zM0 12h4v2h8v-2h4v2h-4v2H4v-2H0z",
]);
export const Braces = icon("Braces", [
  "M3 1h3v2H4v4H2v2h2v4h2v2H3v-1H2v-4H0V6h2V2h1zM10 1h3v1h1v4h2v4h-2v4h-1v1h-3v-2h2V9h2V7h-2V3h-2z",
]);
export const Code2 = icon("Code2", [
  "M4 2h2v3H4v2H2v2h2v2h2v3H4v-2H2v-2H0V6h2V4h2zM10 2h2v2h2v2h2v4h-2v2h-2v2h-2v-3h2V9h2V7h-2V5h-2z",
]);
export const FileJson = icon("FileJson", [
  file,
  "M6 7h1v1H6v1H5V8h1zM9 7h1v1h1v1h-1V8H9zM6 10h1v2H6zM9 10h1v2H9z",
]);
export const List = icon("List", [
  "M1 2h2v2H1zM5 2h10v2H5zM1 7h2v2H1zM5 7h10v2H5zM1 12h2v2H1zM5 12h10v2H5z",
]);
export const ListChecks = icon("ListChecks", [
  "M0 2h1v1h1V1h2v4H1V4H0zM6 2h10v2H6zM0 7h1v1h1V6h2v4H1V9H0zM6 7h10v2H6zM0 12h1v1h1v-2h2v4H1v-1H0zM6 12h10v2H6z",
]);
export const LayoutGrid = icon("LayoutGrid", [], "dashboard");
export const BarChart3 = icon("BarChart3", [], "analytics");
export const Settings2 = icon("Settings2", [], "configuration");
export const GripVertical = icon("GripVertical", [
  "M4 1h2v2H4zM10 1h2v2h-2zM4 7h2v2H4zM10 7h2v2h-2zM4 13h2v2H4zM10 13h2v2h-2z",
]);
export const Menu = icon("Menu", ["M1 2h14v2H1zM1 7h14v2H1zM1 12h14v2H1z"]);
export const Search = icon("Search", [
  "M3 0h6v1h2v2h1v6h-1v2H9v1H3v-1H1V9H0V3h1V1h2zM3 2v1H2v6h1v1h6V9h1V3H9V2z",
  "M11 11h2v2h2v2h1v1h-3v-2h-2z",
]);
export const Eye = icon("Eye", [
  "M5 2h6v1h2v2h2v2h1v2h-1v2h-2v2h-2v1H5v-1H3v-2H1V9H0V7h1V5h2V3h2zM5 4v1H3v2H2v2h1v2h2v1h6v-1h2V9h1V7h-1V5h-2V4z",
  "M6 6h4v4H6z",
]);
export const ToggleLeft = icon("ToggleLeft", [
  "M3 3h10v1h2v2h1v4h-1v2h-2v1H3v-1H1v-2H0V6h1V4h2zM3 5v1H2v4h1v1h10v-1h1V6h-1V5z",
  "M4 6h3v4H4z",
]);
export const Link2 = icon("Link2", [
  "M8 1h6v1h1v6h-1v2h-3V8h2V3H8v2H6V2h2zM2 6h3v2H3v5h5v-2h2v3H8v1H2v-1H1V8h1zM7 5h4v2H9v2H7v2H5V7h2z",
]);
export const GitBranch = icon("GitBranch", [
  "M1 1h5v5H5v4h3V8h2V6H9V1h6v5h-3v4h-2v2H5v3H2V6H1zM3 3v1h1V3zM11 3v1h2V3z",
]);
export const LockKeyhole = icon("LockKeyhole", [lock]);
export const ShieldCheck = icon("ShieldCheck", [
  "M7 0h2v1h4v1h2v8h-1v2h-2v2h-2v1H6v-1H4v-2H2v-2H1V2h2V1h4zM7 2v1H3v6h1v2h2v2h4v-2h2V9h1V3H9V2z",
  "M5 6h2v2h2V6h2v4H7V9H5z",
]);
export const Monitor = icon("Monitor", [
  "M0 1h16v11H0zM2 3v7h12V3zM6 13h4v1h3v2H3v-2h3z",
]);
export const Smartphone = icon("Smartphone", [
  "M4 0h8v1h1v14h-1v1H4v-1H3V1h1zM5 2v10h6V2zM7 13v1h2v-1z",
]);
export const PanelLeftClose = icon("PanelLeftClose", [
  "M0 1h16v14H0zM2 3v10h2V3zM6 3v10h8V3z",
  "M10 5h2v2h-2v2h2v2h-2V9H8V7h2z",
]);
export const LogOut = icon("LogOut", [
  "M1 1h7v2H3v10h5v2H1z",
  "M10 3h2v2h2v2h2v2h-2v2h-2v2h-2V9H6V7h4z",
]);
export const Database = icon("Database", [
  "M3 1h10v1h2v12h-2v1H3v-1H1V2h2zM3 3v1h10V3zM3 6v2h10V6zM3 10v2h10v-2z",
]);
export const PackageCheck = icon("PackageCheck", [
  "M2 1h12v2h2v13H0V3h2zM4 3v2h8V3zM2 7v7h12V7z",
  smallCheck,
]);
export const Heart = icon("Heart", [
  "M2 1h4v1h1v1h2V2h1V1h4v1h1v1h1v5h-1v2h-2v2h-2v2H9v2H7v-2H5v-2H3v-2H1V8H0V3h1V2h1z",
]);
export const Sparkles = icon("Sparkles", [
  "M7 2h2v3h2v2h3v2h-3v2H9v3H7v-3H5V9H2V7h3V5h2zM1 0h2v2H1zM13 0h2v2h-2zM0 13h2v2H0zM14 13h2v2h-2z",
]);
export const Sun = icon("Sun", [
  "M7 0h2v2H7zM7 14h2v2H7zM0 7h2v2H0zM14 7h2v2h-2zM2 2h2v2H2zM12 2h2v2h-2zM2 12h2v2H2zM12 12h2v2h-2zM5 4h6v1h1v6h-1v1H5v-1H4V5h1zM6 6v4h4V6z",
]);
export const Moon = icon("Moon", [
  "M6 0h4v2H8v2H6v6h2v2h6v-2h2v4h-2v1H4v-1H2v-2H1V4h1V2h2V1h2z",
]);
export const Loader2 = icon("Loader2", [
  "M5 0h6v2H5zM12 2h2v2h-2zM14 5h2v6h-2zM12 12h2v2h-2zM5 14h6v2H5zM2 12h2v2H2zM0 5h2v6H0z",
]);
export const Undo2 = icon("Undo2", [
  "M3 1h2v3h6v1h2v2h2v5h-2v2H7v-2h5V7h-1V6H5v3H3V7H1V5H0V4h1V3h2z",
]);
export const Redo2 = icon("Redo2", [
  "M11 1h2v2h2v1h1v1h-1v2h-2v2h-2V6H5v1H4v5h5v2H3v-2H1V7h2V5h2V4h6z",
]);
export const RefreshCw = icon("RefreshCw", [
  "M4 0h8v1h2v2h2v5h-6V6h3V4h-1V3h-1V2H4zM0 8h6v2H3v2h1v1h1v1h7v2H4v-1H2v-2H0z",
]);
